import './Widget.scss'
import {
  T,
  always,
  assoc,
  compose,
  cond,
  curry,
  defaultTo,
  evolve,
  isEmpty,
  keys,
  pick,
  pipe,
  prop,
  reduce,
  tryCatch,
} from 'ramda'
import ImageRenditionPreview from '../../Image/Rendition/Preview'
import LazyComponent from '../../../components/LazyComponent'
import React, { useEffect, useState } from 'react'
import SocialCookieException from '../../../components/Article/SocialCookieException'
import Widget from './index'
import YouTubeWidget from '../../YouTube'
import TikTokWidget from '../../../components/TikTok'
import { playlistEndScreen } from '../../../utilities/media/videos'
import ClientOnly from '../../ClientOnly'

const BrightcoveVideoPlayerWidget = LazyComponent('BrightcoveVideoLazyPlayer')
const FacebookPostWidget = LazyComponent('FacebookPost')
const InstagramPostWidget = LazyComponent('InstagramPost')
const TweetWidget = LazyComponent('Tweet')

// is :: String -> Props -> Boolean
const is = type => ({ component }) => type === component.type

// renderChildren :: (String, [Children]) -> React.Component
const renderChildren = (parentId, children) =>
  children.map((child, index) =>
    <Widget
      component={ child }
      id={ `${parentId}-${index}` }
      key={ `${parentId}-${index}` }
    />,
  )

// getOptionsFromOriginalHtmlMarkup :: String -> Object
const getOptionsFromOriginalHtmlMarkup = tryCatch(
  pipe(
    html => (new DOMParser())
      .parseFromString(html, 'text/html')
      .querySelector('blockquote.twitter-tweet')
    ,
    compose(pick(['cards', 'conversation']), prop('dataset')),
  ),
  always({}),
)

const Tweet = ({ component, nonIABVendorsAuthorized }) =>
  <ClientOnly
    fallback={ <SocialCookieException href={ `https://x.com/i/web/status/${component.id}` } /> }
  >
    { nonIABVendorsAuthorized
      ? <blockquote className="tweet" data-is="component-tweet">
        <TweetWidget tweetId={ component.id } options={ getOptionsFromOriginalHtmlMarkup(component.html) } />
      </blockquote>
      : <SocialCookieException href={ `https://x.com/i/web/status/${component.id}` } />
    }
  </ClientOnly>

const FacebookPost = ({ component, nonIABVendorsAuthorized }) =>
  <ClientOnly fallback={ <SocialCookieException href={ component.href } /> }>
    { nonIABVendorsAuthorized
      ? <FacebookPostWidget href={ component.href } props={ component.props } />
      : <SocialCookieException href={ component.href } />
    }
  </ClientOnly>

const Text = ({ component }) => component.text

const Paragraph = ({ component, id }) =>
  <p
    className={ component.props.className }
    data-is="component-paragraph"
  >
    {renderChildren(id, component.children)}
  </p>

const Link = ({ component, id }) =>
  <a
    data-is="component-link"
    href={ component.props.href }
    title={ component.props.title }
    target={ component.props.target }
    className={ component.props.className }
  >
    {renderChildren(id, component.children)}
  </a>

const CaptionedImage = ({ component }) => {
  const [error, setError] = useState(false)

  return !error
    ? <figure data-is="component-image-wrapper">
      <ImageRenditionPreview
        src={ component.src }
        alt={ component.credit || component.caption }
        onError={ () => setError(true) }
      />
      <figcaption className="text-lighten">
        <span className="caption">
          {component.caption}
        </span>
        <span className="credit">
          {component.credit}
        </span>
      </figcaption>
    </figure>
    : null
}

const CaptionedBrightcoveVideo = ({ component, locale }) =>
  <figure data-is="component-video-wrapper">
    <BrightcoveVideoPlayerWidget
      autoPlay={ component.autoPlay }
      videoId={ component.id }
      endScreenType={ playlistEndScreen }
      locale={ locale }
    />
    <figcaption className="text-lighten">
      <span className="caption">
        {component.description}
      </span>
      { null !== component.credit && !isEmpty(component.credit)
        ? <span className="credit">
          {component.credit}
        </span>
        : null
      }
    </figcaption>
  </figure>

const Heading = ({ component, id }) => React.createElement(
  `h${component.size}`,
  {
    'data-is': 'component-heading',
    className: component.props.className,
  },
  renderChildren(id, component.children),
)

const Emphasis = ({ component, id }) =>
  <em className={ component.props.className } data-is="component-emphasis">
    {renderChildren(id, component.children)}
  </em>

const Strong = ({ component, id }) =>
  <strong className={ component.props.className } data-is="component-strong">
    {renderChildren(id, component.children)}
  </strong>

const Italic = ({ component, id }) =>
  <i className={ component.props.className } data-is="component-italic">
    {renderChildren(id, component.children)}
  </i>

const Bold = ({ component, id }) =>
  <b className={ component.props.className } data-is="component-bold">
    {renderChildren(id, component.children)}
  </b>

const Underline = ({ component, id }) =>
  <u className={ component.props.className } data-is="component-underline">
    {renderChildren(id, component.children)}
  </u>

const Quote = ({ component, id }) =>
  <blockquote className={ component.props.className } data-is="component-quote">
    {renderChildren(id, component.children)}
  </blockquote>

const Figure = ({ component, id }) =>
  <figure className={ component.props.className } data-is="component-figure">
    {renderChildren(id, component.children)}
  </figure>

const Figcaption = ({ component, id }) =>
  <figcaption className={ component.props.className } data-is="component-figcaption">
    {renderChildren(id, component.children)}
  </figcaption>

const Img = ({ component }) =>
  <ImageRenditionPreview
    data-is="component-img"
    className={ component.props.className }
    src={ component.props.src }
    alt={ component.props.alt }
    title={ component.props.title }
  />

const YoutubeVideo = ({ component }) => <YouTubeWidget src={ component.src } />

// TikTokVideo :: Props -> React.Component
const TikTokVideo = ({ component, uploadDate = null }) =>
  <TikTokWidget sourceUrl={ component.sourceUrl } src={ component.src } uploadDate={ uploadDate ?? new Date() } />

// InstagramPost :: Props -> React.Component
const InstagramPost = ({ component, nonIABVendorsAuthorized }) =>
  <ClientOnly fallback={ <SocialCookieException href={ component.href } /> }>
    { nonIABVendorsAuthorized
      ? <InstagramPostWidget postId={ component.id } href={ component.href } />
      : <SocialCookieException href={ component.href } />
    }
  </ClientOnly>

// renameKeys :: {a: b} -> {a: *} -> {b: *}
const renameKeys = curry((keysMap, obj) =>
  reduce((acc, key) => assoc(keysMap[key] || key, obj[key], acc), {}, keys(obj)),
)

// convertPropsToReactProps :: Object -> Object
const convertPropsToReactProps = renameKeys({ 'class': 'className' })

// Widget :: Component -> React.Component
export default pipe(
  evolve({
    component: evolve({
      props: compose(convertPropsToReactProps, defaultTo({})),
    }),
  }),
  cond([
    [is('bold'), Bold],
    [is('brightcoveVideo'), CaptionedBrightcoveVideo],
    [is('emphasis'), Emphasis],
    [is('facebookPost'), FacebookPost],
    [is('figcaption'), Figcaption],
    [is('figure'), Figure],
    [is('subHeading'), Heading],
    [is('captionedImage'), CaptionedImage],
    [is('img'), Img],
    [is('italic'), Italic],
    [is('link'), Link],
    [is('paragraph'), Paragraph],
    [is('quote'), Quote],
    [is('strong'), Strong],
    [is('text'), Text],
    [is('tweet'), Tweet],
    [is('underline'), Underline],
    [is('youtubeVideo'), YoutubeVideo],
    [is('tiktokVideo'), TikTokVideo],
    [is('instagramPost'), InstagramPost],
    [T, always(null)],
  ]),
)
